import React from 'react';
import { Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { formatNumber } from '../../../utils/format';
import { useFlywheelEnabled } from '../../../stores/useOrganizationStore';
import {
  excludedByChannel,
  excludedReasons,
  excludedTotal,
  type FlowContactsPreview,
} from '../../../hooks/flows/flowEnrollment';

const lowerFirst = (text: string) => (text ? text[0].toLowerCase() + text.slice(1) : text);

const SkippedPill: React.FC<{ label: string; count: number }> = ({ label, count }) => (
  <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-foreground/20 bg-background px-2.5 py-0.5 text-foreground">
    {label}
    <span className="font-semibold tabular-nums">{formatNumber(count)}</span>
  </span>
);

// The yellow warning Alert. Content goes in AlertDescription: the Alert styles
// any other first child as its icon, which tinted the whole summary.
const SummaryAlert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Alert variant="warning">
    <AlertDescription className="text-xs sm:text-xs text-foreground">{children}</AlertDescription>
  </Alert>
);

/**
 * What Add to Flow will do with the tagged contacts: how many it can add, then
 * a pill per channel saying why it is closed for the rest.
 */
export const EnrollmentPreviewSummary: React.FC<{
  preview: FlowContactsPreview | null;
  isLoading: boolean;
  isError: boolean;
}> = ({ preview, isLoading, isError }) => {
  const flywheelEnabled = useFlywheelEnabled();
  if (isLoading) {
    return (
      <SummaryAlert>
        <p className="text-muted-foreground">Checking who can be added…</p>
      </SummaryAlert>
    );
  }
  if (isError) {
    return (
      <SummaryAlert>
        <p className="text-muted-foreground">
          Couldn't check consent right now. Add to Flow still adds only contacts it can reach.
        </p>
      </SummaryAlert>
    );
  }
  if (!preview || preview.selected_contacts === 0) return null;

  const selected = preview.selected_contacts;
  const eligible = preview.eligible_contacts;
  const skipped = excludedTotal(preview.excluded);
  // SMS is a Flywheel-only channel; an org without it gets no SMS reason.
  const byChannel = excludedByChannel(preview.excluded_by_channel).filter(
    (c) => flywheelEnabled || c.key !== 'sms'
  );
  const noun = selected === 1 ? 'contact' : 'contacts';

  // One pill per channel and reason, e.g. "Calls: not opted in 1". Older
  // responses without the per-channel split fall back to the overall reasons.
  const pills =
    byChannel.length > 0
      ? byChannel.flatMap((c) =>
          c.reasons.map((r) => ({
            key: `${c.channel}-${r.label}`,
            label: `${c.channel}: ${lowerFirst(r.label)}`,
            count: r.count,
          }))
        )
      : excludedReasons(preview.excluded).map((r) => ({
          key: r.label,
          label: r.label,
          count: r.count,
        }));

  return (
    <SummaryAlert>
      <div className="space-y-2">
        <p>
          {skipped === 0 ? (
            <>
              All <span className="font-semibold text-foreground">{formatNumber(selected)}</span>{' '}
              {noun} can be added to this flow
            </>
          ) : (
            <>
              <span className="font-semibold text-foreground">
                {formatNumber(eligible)} of {formatNumber(selected)}
              </span>{' '}
              {noun} can be added to this flow
            </>
          )}
        </p>
        {skipped > 0 && pills.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span>Skipped:</span>
            {pills.map((p) => (
              <SkippedPill key={p.key} label={p.label} count={p.count} />
            ))}
          </div>
        )}
      </div>
    </SummaryAlert>
  );
};
